// apps/web/src/pages/author/constraint-config/dependency-config.tsx
//
// Phase 9 Plan 07 Task 2 — Dependency constraint authoring sub-form
// (CSTR-01 partial + CSTR-05 explanation surface).
//
// Schema source-of-truth (packages/shared/src/schemas/constraint.ts lines 64-72):
//   { kind: "dependency";
//     if: { ref: StateRef, equals: unknown };
//     require: StateRef { widgetInstanceId, pathInState };
//     explanation: 1-500 chars }
//
// UI-SPEC §"Dependency" copy table (lines 494-501):
//   Helper: "Selecting one option enables or disables another option's selection."
//   Source label: "When this option is selected"
//   Target label: "Target option"
//
// equals editor — v1 per D-22 + Q-09 (plan body line 100): plain Input +
// "Parse as JSON" Checkbox toggle (re-uses EqualsEditor from visibility-config.tsx).
//
// authr-06 hygiene: per-path useWatch ONLY. NEVER top-level.

import { useFormContext } from "react-hook-form";
import { Fieldset } from "@/components/ui/fieldset";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { ConstraintConfigSubFormProps } from "./types.js";
import { EqualsEditor } from "./visibility-config.js";

// =============================================================================
// DependencyConfigShape — local typing for the default factory.
// =============================================================================

export interface DependencyConfigShape {
  kind: "dependency";
  if: {
    ref: { widgetInstanceId: string; pathInState: string };
    equals: unknown;
  };
  require: { widgetInstanceId: string; pathInState: string };
  explanation: string;
}

// =============================================================================
// Default factory
// =============================================================================

export function defaultDependencyConfig(): DependencyConfigShape {
  return {
    kind: "dependency",
    if: {
      ref: { widgetInstanceId: "", pathInState: "" },
      equals: "",
    },
    require: { widgetInstanceId: "", pathInState: "" },
    explanation: "",
  };
}

// =============================================================================
// DependencyConfigSubForm
// =============================================================================

export function DependencyConfigSubForm({
  constraintId: _constraintId,
  basePath,
}: ConstraintConfigSubFormProps): React.ReactElement {
  const { control } = useFormContext();

  return (
    <div className="flex flex-col gap-4">
      <Fieldset legend="DEPENDENCY">
        <p className="text-xs text-muted-foreground">
          Selecting one option enables or disables another option's selection.
        </p>

        <Fieldset legend="WHEN THIS OPTION IS SELECTED">
          <FormField
            control={control}
            name={`${basePath}.if.ref.widgetInstanceId`}
            render={({ field: f }) => (
              <FormItem data-field-path={`${basePath}.if.ref.widgetInstanceId`}>
                <FormLabel>Source widget</FormLabel>
                <FormControl>
                  <Input
                    {...f}
                    value={(f.value as string | undefined) ?? ""}
                    placeholder="widget-instance-id"
                    data-testid="dependency-if-widget"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={`${basePath}.if.ref.pathInState`}
            render={({ field: f }) => (
              <FormItem data-field-path={`${basePath}.if.ref.pathInState`}>
                <FormLabel>Source state path</FormLabel>
                <FormControl>
                  <Input
                    {...f}
                    value={(f.value as string | undefined) ?? ""}
                    placeholder="selectedOption"
                    data-testid="dependency-if-path"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <EqualsEditor
            path={`${basePath}.if.equals`}
            testIdPrefix="dependency-if-equals"
            label="equals"
          />
        </Fieldset>

        <Fieldset legend="THEN REQUIRE THIS STATE TO BE SET">
          <FormField
            control={control}
            name={`${basePath}.require.widgetInstanceId`}
            render={({ field: f }) => (
              <FormItem data-field-path={`${basePath}.require.widgetInstanceId`}>
                <FormLabel>Target widget</FormLabel>
                <FormControl>
                  <Input
                    {...f}
                    value={(f.value as string | undefined) ?? ""}
                    placeholder="widget-instance-id"
                    data-testid="dependency-require-widget"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={`${basePath}.require.pathInState`}
            render={({ field: f }) => (
              <FormItem data-field-path={`${basePath}.require.pathInState`}>
                <FormLabel>Target state path</FormLabel>
                <FormControl>
                  <Input
                    {...f}
                    value={(f.value as string | undefined) ?? ""}
                    placeholder="ready"
                    data-testid="dependency-require-path"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Fieldset>
      </Fieldset>

      <Fieldset legend="EXPLANATION">
        <FormField
          control={control}
          name={`${basePath}.explanation`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.explanation`}>
              <FormLabel>Explanation</FormLabel>
              <FormControl>
                <Textarea
                  {...f}
                  value={(f.value as string | undefined) ?? ""}
                  rows={3}
                  maxLength={500}
                  placeholder="Shown to authors and viewers when this rule disables an option. Required."
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </Fieldset>
    </div>
  );
}
DependencyConfigSubForm.displayName = "DependencyConfigSubForm";
